/**
 * OnlyFans messages (as the gateway's /v1/messages/get returns them, enriched
 * by the CRM with operator attribution) turned into what the Transcript draws.
 * Field fallbacks follow the CRM chat window (crm/src/public/chats/chats.js),
 * which has met every shape the gateway sends.
 */

type Raw = Record<string, unknown>;

export type MediaType = 'photo' | 'video' | 'audio' | 'gif';

export interface ChatMedia {
  id: string;
  type: MediaType;
  thumb: string | null;
  src: string | null;
  canView: boolean;
  /** Shown free inside a PPV (OnlyFans' `previews`). */
  preview: boolean;
  duration?: number;
}

/** How an AI message went out; plain replies are `reply`. */
export type AiSendKind = 'reply' | 'follow_up' | 'welcome' | 'mass' | 'priority_mass' | 'online';
const AI_SEND_KINDS: readonly string[] = ['reply', 'follow_up', 'welcome', 'mass', 'priority_mass', 'online'];

/** `ai`: the brain or the mass-message service sent it; `model`: creator side, sender unknown (sent straight on OnlyFans…). */
export type Sender = { kind: 'fan' } | { kind: 'me' } | { kind: 'teammate'; username: string } | { kind: 'ai'; via: AiSendKind } | { kind: 'model' };

export interface ChatMessage {
  /** Gateway message id (numeric OnlyFans id as a string). */
  id: string;
  side: 'fan' | 'creator';
  sender: Sender;
  text: string;
  createdAt: number;
  media: ChatMedia[];
  ppv: { price: number; bought: boolean } | null;
  tip: { amount: number } | null;
  isRead: boolean | null;
  giphyId: string | null;
  /** The creator liked this (fan) message on OnlyFans. */
  liked?: boolean;
  /** A send still on its way (shown at once, like the CRM's temp row), or one that failed. */
  pending?: { state: 'sending' | 'failed'; error?: string };
}

/**
 * What a content message cost, on the message itself (not only under the last
 * message of a run): "$24.99 · Unlocked", "$10.50 · Locked" or "Free" — for the
 * fan's media too (always free).
 */
export function priceState(m: ChatMessage): { kind: 'unlocked' | 'locked' | 'free'; price: number } | null {
  if (m.tip) return null;
  if (m.ppv) return { kind: m.ppv.bought ? 'unlocked' : 'locked', price: m.ppv.price };
  return m.media.length > 0 ? { kind: 'free', price: 0 } : null;
}

export interface MessageContext {
  fanId: string;
  /** The CRM username of the person signed in ("You" on their sends). */
  myUsername: string;
}

function obj(v: unknown): Raw | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : null;
}
function first(...values: unknown[]): unknown {
  return values.find((v) => v !== undefined && v !== null && v !== '');
}
function str(v: unknown): string {
  return v === undefined || v === null ? '' : String(v);
}
function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}
function url(v: unknown): string | null {
  if (typeof v === 'string' && v) return v;
  const o = obj(v);
  return o && typeof o.url === 'string' && o.url ? o.url : null;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };

/** Gateway text may carry HTML (<p>, <br>, entities): plain text with line breaks. */
export function plainText(html: unknown): string {
  return str(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (m, code: string) => {
      const c = code.toLowerCase();
      if (c.startsWith('#x')) return String.fromCodePoint(parseInt(c.slice(2), 16));
      if (c.startsWith('#') && c !== '#39') return String.fromCodePoint(Number(c.slice(1)));
      return ENTITIES[c] ?? m;
    })
    .trim();
}

/** First numeric id among the gateway's id fields. */
export function messageId(m: Raw): string {
  for (const v of [m.message_id, m.of_message_id, m.ofMessageId, m.id, m.queueId]) {
    const s = str(v).trim();
    if (/^\d+$/.test(s)) return s;
  }
  return str(first(m.id, m.message_id, m.queueId));
}

function isCreatorSide(m: Raw, fanId: string): boolean {
  const from = obj(m.fromUser) || obj(m.from_user);
  if (from?.isPerformer === true || m.isFromAccount === true || m.isMe === true || m.type === 'sent' || m.sender_type === 'chatter') return true;
  if (m.type === 'received' || m.sender_type === 'fan' || m.isMe === false) return false;
  const senderId = str(first(from?.id, m.user_id));
  return senderId ? senderId !== String(fanId) : false;
}

function mediaType(item: Raw): MediaType {
  const t = str(first(item.type, item.fileType, item.mediaType, item.media_type, item.kind)).toLowerCase();
  if (t.includes('gif')) return 'gif';
  if (t.includes('video')) return 'video';
  if (t.includes('audio') || t.includes('voice')) return 'audio';
  if (t.includes('photo') || t.includes('image')) return 'photo';
  const mime = str(first(item.mimeType, item.mime_type, item.contentType)).toLowerCase();
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if ((num(item.duration) ?? 0) > 0) return 'video';
  return 'photo';
}

/** Ids of the media a PPV shows for free. */
function previewIdsOf(m: Raw): Set<string> {
  const list = first(m.previews, m.preview_media_files, m.previewMediaFiles);
  if (!Array.isArray(list)) return new Set();
  return new Set(list.map((x) => str(obj(x)?.id ?? x)).filter(Boolean));
}

function mediaOf(m: Raw, price: number, bought: boolean): ChatMedia[] {
  const list = first(m.media_files, m.mediaFiles, m.media, m.attachments, obj(m.content)?.media_files);
  if (!Array.isArray(list)) return [];
  const previews = price > 0 ? previewIdsOf(m) : new Set<string>();
  return list.map((entry) => {
    const item: Raw = obj(entry) || { id: entry };
    const files = obj(item.files) || {};
    const type = mediaType(item);
    const full = url(files.full) || url(files.source) || null;
    const thumb =
      type === 'audio'
        ? null
        : url(files.thumb) || url(files.squarePreview) || url(files.preview) || url(obj(item.thumbs)?.w480) || url(item.thumb) || url(item.preview) || null;
    const flag = first(item.canView, item.canview, item.can_view);
    const canView = typeof flag === 'boolean' ? flag : price === 0 || bought || !!full;
    const src = canView ? (type === 'audio' ? url(item.playUrl) || full || url(item.src) || url(item.url) : full || url(item.src) || url(item.url)) : null;
    const id = str(item.id);
    const out: ChatMedia = { id, type, thumb: thumb ?? (type === 'audio' ? null : src), src, canView, preview: previews.has(id) };
    if (type === 'audio' || type === 'video') {
      const d = num(first(item.duration, item.durationInSeconds, item.length));
      if (d != null) out.duration = d;
    }
    return out;
  });
}

export function normalizeMessage(raw: unknown, ctx: MessageContext): ChatMessage {
  const m = obj(raw) || {};
  const creator = isCreatorSide(m, ctx.fanId);
  const operator = str(m.operator_username).trim();
  const sender: Sender = !creator
    ? { kind: 'fan' }
    : operator
      ? operator === ctx.myUsername
        ? { kind: 'me' }
        : { kind: 'teammate', username: operator }
      : m.sent_by_ai === true
        ? { kind: 'ai', via: AI_SEND_KINDS.includes(str(m.ai_send_kind)) ? (str(m.ai_send_kind) as AiSendKind) : 'reply' }
        : { kind: 'model' };

  const isTip = m.isTip === true || m.is_tip === true;
  const tipAmount = isTip ? num(first(m.tipAmount, m.tip_amount, m.price, m.amount)) : null;
  const price = isTip ? 0 : num(first(m.price, m.amount, m.purchasePrice, m.unlock_price, m.unlockPrice)) ?? 0;
  const paid = price > 0 || m.is_paid === true || m.isPaid === true || m.locked === true;
  const bought =
    m.is_paid === true ||
    m.isPaid === true ||
    m.purchased === true ||
    m.unlocked === true ||
    m.isOpened === true ||
    m.is_opened === true ||
    first(m.canPurchaseReason, m.can_purchase_reason) === 'opened';
  const created = Date.parse(str(first(m.createdAt, m.created_at, m.date, m.timestamp, m.created)));
  const read = first(m.is_read, m.isRead, m.read);
  const giphy = first(m.giphyId, m.giphy_id, m.gifId, m.gif_id, obj(m.giphy)?.id);

  return {
    id: messageId(m),
    side: creator ? 'creator' : 'fan',
    sender,
    text: plainText(first(m.text, m.message, m.content, m.body)),
    createdAt: Number.isFinite(created) ? created : 0,
    media: mediaOf(m, price, bought),
    ppv: paid && price > 0 ? { price, bought } : null,
    tip: isTip ? { amount: tipAmount ?? 0 } : null,
    isRead: typeof read === 'boolean' ? read : null,
    giphyId: giphy == null ? null : str(giphy),
    liked: first(m.isLiked, m.is_liked, m.isFavorite, m.is_favorite) === true,
  };
}

/** Where the next older page starts, or null at the start of the chat. */
export type PageCursor = { offset: number } | { beforeId: string } | null;

export interface MessagesPage {
  /** Oldest first. */
  messages: ChatMessage[];
  next: PageCursor;
}

function byTime(a: ChatMessage, b: ChatMessage): number {
  return a.createdAt - b.createdAt || Number(a.id) - Number(b.id);
}

export function parseMessagesPage(body: unknown, ctx: MessageContext): MessagesPage {
  const outer = obj(body) || {};
  const resp = obj(outer.data) || outer;
  const list = [resp.list, resp.messages, resp.items, outer.list, outer.messages, outer.items].find(Array.isArray) as unknown[] | undefined;
  const messages = (list || []).map((m) => normalizeMessage(m, ctx)).filter((m) => m.id).sort(byTime);
  const hasMoreRaw = first(resp.hasMore, resp.has_more);
  const hasMore = typeof hasMoreRaw === 'boolean' ? hasMoreRaw : messages.length > 0;
  let next: PageCursor = null;
  if (hasMore && messages.length) {
    const offset = num(first(resp.nextOffset, resp.next_offset));
    next = offset != null ? { offset } : { beforeId: messages[0]!.id };
  }
  return { messages, next };
}

/** Thread oldest first; a message seen again replaces the older copy (read/bought flags change). */
export function mergeMessages(existing: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(existing.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort(byTime);
}
